// Auth API calls. Import and call these from stores only never from components.

import { apiClient } from '@/api/client'
import type { ApiResponse, ConsentInput, User, UserRole } from '@/types'

export interface LoginRequest {
  email: string
  password: string
  role: string
}

export interface RegisterRequest {
  email: string
  password: string
  confirm_password: string
  full_name: string
  consents: ConsentInput[]
}

interface LoginResponse {
  access_token: string
  user: User
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
  confirm_password: string
}

export interface ResendVerificationRequest {
  email: string
}

export interface ChangePasswordRequest {
  old_password: string
  new_password: string
  confirm_password: string
}

export interface RegisterCompanyRequest {
  email: string
  password: string
  confirm_password: string
  full_name: string
  company_name: string
  consents: ConsentInput[]
}

interface MeResponse {
  user: {
    id: string
    email: string
    role: UserRole
    is_active: boolean
    email_verified_at: string | null
    last_login_at: string | null
    company_id?: string | null
  }
}

export const authApi = {
  login: (data: LoginRequest) =>
    apiClient.post<ApiResponse<LoginResponse>>('/auth/login', data, { withCredentials: true }),

  register: (data: RegisterRequest) =>
    apiClient.post<ApiResponse<{ message: string }>>('/auth/register', data),

  registerCompany: (data: RegisterCompanyRequest) =>
    apiClient.post<ApiResponse<{ message: string }>>('/auth/register/company', data),

  // Uses httpOnly cookie — withCredentials required to send the cookie
  refresh: () =>
    apiClient.post<ApiResponse<{ access_token: string }>>('/auth/refresh', null, { withCredentials: true }),

  logout: () =>
    apiClient.post<ApiResponse<{ message: string }>>('/auth/logout', null, { withCredentials: true }),

  verifyEmail: (token: string) =>
    apiClient.get<ApiResponse<{ message: string }>>(`/auth/verify-email?token=${token}`),

  forgotPassword: (data: ForgotPasswordRequest) =>
    apiClient.post<ApiResponse<{ message: string }>>('/auth/forgot-password', data),

  resetPassword: (data: ResetPasswordRequest) =>
    apiClient.post<ApiResponse<{ message: string }>>('/auth/reset-password', data),

  resendVerification: (data: ResendVerificationRequest) =>
    apiClient.post<ApiResponse<{ message: string }>>('/auth/resend-verification', data),

  // GET /auth/me — reads JWT claims, no refresh rotation.
  getMe: () =>
    apiClient.get<ApiResponse<MeResponse>>('/auth/me'),

  // Requires authentication — sends PATCH /api/v1/password
  changePassword: (data: ChangePasswordRequest) =>
    apiClient.patch<ApiResponse<{ message: string }>>('/password', data),

  // Call this to initialize the CSRF token cookie before login/register
  initializeCSRF: () =>
    apiClient.get('/livez'),
}

